import { supabase } from '../lib/supabase';

/**
 * Video Service
 * Handles all video watching and tracking related operations
 */
export const videoService = {
  /**
   * Start a new video watch session
   * @param {Object} videoData - Video information
   * @returns {Promise<Object>} Created session data
   */
  async startWatchSession(videoData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      const { data, error } = await supabase
        .from('video_watch_sessions')
        .insert({
          user_id: user.id,
          video_id: videoData.videoId,
          video_title: videoData.videoTitle,
          course_id: videoData.courseId,
          total_video_duration: videoData.totalDuration,
          started_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Start video watch session error:', error);
      throw error;
    }
  },

  /**
   * Update an existing video watch session
   * @param {string} sessionId - Session ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated session data
   */
  async updateWatchSession(sessionId, updateData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      // Calculate watch percentage if duration is provided
      let additionalData = {};
      if (updateData.duration_seconds && updateData.total_video_duration) {
        const percentage = (updateData.duration_seconds / updateData.total_video_duration) * 100;
        additionalData = {
          watch_percentage: Math.min(100, Math.round(percentage)),
          is_completed: percentage >= 95 // Mark as completed if watched 95% or more
        };
      }

      const { data, error } = await supabase
        .from('video_watch_sessions')
        .update({
          ...updateData,
          ...additionalData,
          ended_at: updateData.ended_at || new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update video watch session error:', error);
      throw error;
    }
  },

  /**
   * End a video watch session
   * @param {string} sessionId - Session ID
   * @param {number} watchedSeconds - Total seconds watched
   * @param {number} totalDuration - Total video duration in seconds
   * @returns {Promise<Object>} Updated session data
   */
  async endWatchSession(sessionId, watchedSeconds, totalDuration) {
    return this.updateWatchSession(sessionId, {
      duration_seconds: watchedSeconds,
      total_video_duration: totalDuration,
      ended_at: new Date().toISOString()
    });
  },

  /**
   * Get video watch statistics for a user
   * @param {string} videoId - Optional video ID to filter by
   * @returns {Promise<Object>} Video watch statistics
   */
  async getWatchStats(videoId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('User not authenticated for watch stats');
        return [];
      }

      let query = supabase
        .from('video_watch_stats')
        .select('*')
        .eq('user_id', user.id);

      if (videoId) {
        query = query.eq('video_id', videoId);
      }

      const { data, error } = await query;

      if (error) {
        // If table doesn't exist, return empty array instead of throwing
        if (error.code === '42P01') {
          console.log('video_watch_stats table does not exist yet');
          return [];
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error('Get video watch stats error:', error);
      return [];
    }
  },

  /**
   * Get user's total video watch time
   * @returns {Promise<Object>} Total watch time statistics
   */
  async getTotalWatchTime() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      const { data, error } = await supabase
        .rpc('get_user_total_video_time', { p_user_id: user.id });

      if (error) throw error;
      return data[0] || {
        total_seconds: 0,
        total_minutes: 0,
        total_hours: 0,
        video_count: 0,
        completed_videos: 0
      };
    } catch (error) {
      console.error('Get total watch time error:', error);
      throw error;
    }
  },

  /**
   * Get recent video watch sessions
   * @param {number} limit - Number of sessions to fetch
   * @returns {Promise<Array>} Recent watch sessions
   */
  async getRecentSessions(limit = 10) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      const { data, error } = await supabase
        .from('video_watch_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get recent sessions error:', error);
      throw error;
    }
  },

  /**
   * Check if a video has been completed by the user
   * @param {string} videoId - Video ID
   * @returns {Promise<boolean>} True if video is completed
   */
  async isVideoCompleted(videoId) {
    try {
      const stats = await this.getWatchStats(videoId);
      return stats && stats.length > 0 && stats[0].is_completed;
    } catch (error) {
      console.error('Check video completion error:', error);
      return false;
    }
  },

  /**
   * End all learning sessions (placeholder for compatibility)
   * This method is called from videooverview but not needed for video tracking
   */
  endAllLearningSessions() {
    console.log('endAllLearningSessions called - no action needed');
    // This is a placeholder method for backward compatibility
    // Video sessions are handled individually per video
  },

  /**
   * Get list of videos
   * @param {Object} options - Query options
   * @returns {Promise<Array>} List of videos
   */
  async getVideosList({ limit = 10 } = {}) {
    // Return hardcoded videos for local development
    return [
      {
        id: '64YkJcWh8ns',
        title: '生成AI Boost初級編第1回',
        description: 'AIの基礎知識と活用方法',
        duration: 3344,
        thumbnail_url: '/video-icon.png',
        youtube_id: '64YkJcWh8ns',
        created_at: '2024-01-01T00:00:00.000Z',
        formatted_duration: '55:44',
        agenda: [
          { time: '00:00', title: 'はじめに' },
          { time: '01:30', title: '生成AIとは' },
          { time: '03:00', title: '基本的な使い方' }
        ]
      },
      {
        id: '52VJJMYYkMA',
        title: '生成AI Boost初級編第2回',
        description: 'ChatGPTを使った実践的な活用方法',
        duration: 4085,
        thumbnail_url: '/video-icon.png',
        youtube_id: '52VJJMYYkMA',
        created_at: '2024-01-02T00:00:00.000Z',
        formatted_duration: '1:08:05',
        agenda: [
          { time: '00:00', title: 'イントロダクション' },
          { time: '02:00', title: '実践的なプロンプト' },
          { time: '05:00', title: 'まとめ' }
        ]
      },
      {
        id: 'kk7GlxWZAfk',
        title: '生成AI Boost初級編第3回',
        description: '効果的なプロンプトの書き方',
        duration: 5760,
        thumbnail_url: '/video-icon.png',
        youtube_id: 'kk7GlxWZAfk',
        created_at: '2024-01-03T00:00:00.000Z',
        formatted_duration: '1:36:00',
        agenda: [
          { time: '00:00', title: 'プロンプトエンジニアリングとは' },
          { time: '03:00', title: '効果的なテクニック' },
          { time: '07:00', title: '実際の活用例' }
        ]
      }
    ].slice(0, limit);
  },

  /**
   * Get video details
   * @param {string} videoId - Video ID
   * @returns {Promise<Object>} Video details
   */
  async getVideoDetails(videoId) {
    const videos = await this.getVideosList({ limit: 100 });
    const video = videos.find(v => v.id === videoId);
    return video || {
      id: videoId,
      title: 'Unknown Video',
      description: 'Video not found',
      duration: 0,
      url: ''
    };
  },

  /**
   * Get video progress
   * @param {string} videoId - Video ID
   * @returns {Promise<Object>} Progress data
   */
  async getProgress(videoId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageから取得
        const localProgress = localStorage.getItem(`video_progress_${videoId}`);
        if (localProgress) {
          const parsed = JSON.parse(localProgress);
          return {
            progress_percent: parsed.progress_percent || 0,
            current_time: parsed.current_time || 0
          };
        }
        return { progress_percent: 0, current_time: 0 };
      }

      // video_progressテーブルから最新の再生位置を取得
      const { data: progressData, error: progressError } = await supabase
        .from('video_progress')
        .select('current_time_seconds, progress_percent')
        .eq('user_id', user.id)
        .eq('video_id', videoId)
        .single();

      if (progressError && progressError.code !== 'PGRST116') {
        console.error('Error fetching video progress:', progressError);
      }

      if (progressData) {
        return {
          progress_percent: progressData.progress_percent || 0,
          current_time: progressData.current_time_seconds || 0
        };
      }

      // フォールバック: 統計テーブルから取得
      const stats = await this.getWatchStats(videoId);
      if (stats && stats.length > 0) {
        return {
          progress_percent: stats[0].highest_watch_percentage || 0,
          current_time: 0 // 統計テーブルには再生位置情報がない
        };
      }
    } catch (error) {
      console.error('Get progress error:', error);
    }
    return {
      progress_percent: 0,
      current_time: 0
    };
  },

  /**
   * Get progress for multiple videos
   * @param {Array<string>} videoIds - Array of video IDs
   * @returns {Promise<Object>} Progress map
   */
  async getMultipleProgress(videoIds) {
    const progressMap = {};
    for (const id of videoIds) {
      progressMap[id] = await this.getProgress(id);
    }
    return progressMap;
  },

  /**
   * Record video progress
   * @param {string} videoId - Video ID
   * @param {number} currentTime - Current time in seconds
   * @param {number} duration - Total duration in seconds
   * @returns {Promise<void>}
   */
  async recordProgress(videoId, currentTime, duration) {
    try {
      const progressPercent = Math.min(100, Math.round((currentTime / duration) * 100));
      const progressData = {
        progress_percent: progressPercent,
        current_time: currentTime,
        updated_at: new Date().toISOString()
      };

      // LocalStorageに保存（フォールバック用）
      localStorage.setItem(`video_progress_${videoId}`, JSON.stringify(progressData));

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageのみ
        return;
      }

      // video_progressテーブルにupsert
      const { error } = await supabase
        .from('video_progress')
        .upsert({
          user_id: user.id,
          video_id: videoId,
          current_time_seconds: currentTime,
          progress_percent: progressPercent,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,video_id'
        });

      if (error) {
        console.error('Error saving video progress to DB:', error);
      }
    } catch (error) {
      console.error('Record progress error:', error);
    }
  },

  /**
   * Get video memo
   * @param {string} videoId - Video ID (YouTube ID)
   * @returns {Promise<string>} Memo text
   */
  async getMemo(videoId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageから取得
        return localStorage.getItem(`video_memo_${videoId}`) || '';
      }

      const { data, error } = await supabase
        .from('video_memos')
        .select('memo')
        .eq('user_id', user.id)
        .eq('youtube_video_id', videoId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching memo:', error);
        return localStorage.getItem(`video_memo_${videoId}`) || '';
      }

      return data?.memo || '';
    } catch (error) {
      console.error('Get memo error:', error);
      return localStorage.getItem(`video_memo_${videoId}`) || '';
    }
  },

  /**
   * Save video memo
   * @param {string} videoId - Video ID (YouTube ID)
   * @param {string} memo - Memo text
   * @returns {Promise<void>}
   */
  async saveMemo(videoId, memo) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageに保存
        localStorage.setItem(`video_memo_${videoId}`, memo);
        return;
      }

      // まず既存のメモがあるか確認
      const { data: existingMemo } = await supabase
        .from('video_memos')
        .select('id')
        .eq('user_id', user.id)
        .eq('youtube_video_id', videoId)
        .single();

      if (existingMemo) {
        // 更新
        const { error } = await supabase
          .from('video_memos')
          .update({
            memo: memo,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('youtube_video_id', videoId);

        if (error) throw error;
      } else {
        // 新規作成
        const { error } = await supabase
          .from('video_memos')
          .insert({
            user_id: user.id,
            youtube_video_id: videoId,
            memo: memo,
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      // 成功したらlocalStorageも更新（バックアップ用）
      localStorage.setItem(`video_memo_${videoId}`, memo);
    } catch (error) {
      console.error('Save memo error:', error);
      // エラー時はlocalStorageに保存
      localStorage.setItem(`video_memo_${videoId}`, memo);
    }
  },

  /**
   * Get memo category for a video
   * @param {string} videoId - Video ID (YouTube ID)
   * @returns {Promise<string>} Category name
   */
  async getMemoCategory(videoId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageから取得
        return localStorage.getItem(`memoCategory:${videoId}`) || '未分類';
      }

      const { data, error } = await supabase
        .from('video_memos')
        .select('category')
        .eq('user_id', user.id)
        .eq('youtube_video_id', videoId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching memo category:', error);
        return localStorage.getItem(`memoCategory:${videoId}`) || '未分類';
      }

      return data?.category || '未分類';
    } catch (error) {
      console.error('Get memo category error:', error);
      return localStorage.getItem(`memoCategory:${videoId}`) || '未分類';
    }
  },

  /**
   * Save memo category for a video
   * @param {string} videoId - Video ID (YouTube ID)
   * @param {string} category - Category name
   * @returns {Promise<void>}
   */
  async saveMemoCategory(videoId, category) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageに保存
        localStorage.setItem(`memoCategory:${videoId}`, category);
        return;
      }

      // まず既存のメモがあるか確認
      const { data: existingMemo } = await supabase
        .from('video_memos')
        .select('id')
        .eq('user_id', user.id)
        .eq('youtube_video_id', videoId)
        .single();

      if (existingMemo) {
        // 更新
        const { error } = await supabase
          .from('video_memos')
          .update({
            category: category,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('youtube_video_id', videoId);

        if (error) throw error;
      } else {
        // 新規作成（メモが空でもカテゴリだけ保存）
        const { error } = await supabase
          .from('video_memos')
          .insert({
            user_id: user.id,
            youtube_video_id: videoId,
            memo: '',
            category: category,
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      // 成功したらlocalStorageも更新（バックアップ用）
      localStorage.setItem(`memoCategory:${videoId}`, category);
    } catch (error) {
      console.error('Save memo category error:', error);
      // エラー時はlocalStorageに保存
      localStorage.setItem(`memoCategory:${videoId}`, category);
    }
  },

  /**
   * Get user's custom memo categories
   * @returns {Promise<string[]>}
   */
  async getUserMemoCategories() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageから取得
        const stored = localStorage.getItem('userMemoCategories');
        return stored ? JSON.parse(stored) : [];
      }

      const { data, error } = await supabase
        .from('user_memo_categories')
        .select('category_name')
        .eq('user_id', user.id)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching user memo categories:', error);
        const stored = localStorage.getItem('userMemoCategories');
        return stored ? JSON.parse(stored) : [];
      }

      const categories = data?.map(item => item.category_name) || [];
      
      // 成功したらlocalStorageも更新（バックアップ用）
      localStorage.setItem('userMemoCategories', JSON.stringify(categories));
      
      return categories;
    } catch (error) {
      console.error('Get user memo categories error:', error);
      const stored = localStorage.getItem('userMemoCategories');
      return stored ? JSON.parse(stored) : [];
    }
  },

  /**
   * Save user's custom memo categories
   * @param {string[]} categories - Array of category names
   * @returns {Promise<void>}
   */
  async saveUserMemoCategories(categories) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // ユーザーがログインしていない場合はlocalStorageに保存
        localStorage.setItem('userMemoCategories', JSON.stringify(categories));
        return;
      }

      // 既存のカテゴリーを全削除
      const { error: deleteError } = await supabase
        .from('user_memo_categories')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // 新しいカテゴリーを追加
      if (categories.length > 0) {
        const categoriesToInsert = categories.map((category, index) => ({
          user_id: user.id,
          category_name: category,
          display_order: index
        }));

        const { error: insertError } = await supabase
          .from('user_memo_categories')
          .insert(categoriesToInsert);

        if (insertError) throw insertError;
      }

      // 成功したらlocalStorageも更新（バックアップ用）
      localStorage.setItem('userMemoCategories', JSON.stringify(categories));
    } catch (error) {
      console.error('Save user memo categories error:', error);
      // エラー時はlocalStorageに保存
      localStorage.setItem('userMemoCategories', JSON.stringify(categories));
      throw error;
    }
  },

  /**
   * Start a learning session
   * @param {string} videoId - Video ID
   * @returns {Promise<void>}
   */
  async startLearningSession(videoId) {
    console.log(`Starting learning session for video: ${videoId}`);
    // This is handled by VideoPlayer component when video actually plays
  },

  /**
   * End a learning session
   * @param {string} videoId - Video ID
   * @returns {Promise<void>}
   */
  async endLearningSession(videoId) {
    console.log(`Ending learning session for video: ${videoId}`);
    // Mock implementation for local development
  }
};